import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { organizationTokenFromId } from "../../services/teleconsultoriaScheduleService";
import {
  approveAffiliationRequest,
  discardAffiliationRequest,
} from "../services/affiliationRequestService";
import { AffiliationRuleError } from "../services/affiliationRules";
import {
  createAffiliation,
  listProvidedAffiliations,
  setAffiliationActive,
} from "../services/teleconsultoriaAffiliationService";
import type {
  AffiliationActionResult,
  AffiliationOrganization,
  TeleconsultoriaAffiliationRow,
} from "../types";

/** Referência estável: evita loop de `reset` no useAsyncTask. */
const EMPTY_ROWS: TeleconsultoriaAffiliationRow[] = [];

const GENERIC_ERROR_MESSAGE = "Não foi possível salvar. Tente novamente.";

/** Regra de negócio mostra a mensagem própria; erro técnico vira texto genérico. */
function toErrorMessage(error: unknown): string {
  if (error instanceof AffiliationRuleError) return error.message;
  console.error("useProvidedAffiliations", error);
  return GENERIC_ERROR_MESSAGE;
}

/**
 * Visão da PRESTADORA: lista, adiciona, ativa/desativa as organizações atendidas
 * e responde aos pedidos das afiliadas (aprovar/recusar).
 * A prestadora é sempre a organização logada.
 */
export function useProvidedAffiliations() {
  const { access } = useSessionContext();
  const { organization } = useOrganizationContext();
  const providerToken = organizationTokenFromId(access?.organizationId);
  /** Linha com ativação em andamento (o switch dela mostra loading). */
  const [pendingRowId, setPendingRowId] = useState<string | null>(null);

  const provider = useMemo<AffiliationOrganization>(
    () => ({ token: providerToken, name: organization?.name || "" }),
    [providerToken, organization?.name],
  );

  const loadTask = useAsyncTask({
    task: useCallback(
      () => listProvidedAffiliations(providerToken),
      [providerToken],
    ),
    initialData: EMPTY_ROWS,
  });
  const createTask = useAsyncTask({
    task: useCallback(
      (receiver: AffiliationOrganization) =>
        createAffiliation({ provider, receiver }),
      [provider],
    ),
  });
  const toggleTask = useAsyncTask({ task: setAffiliationActive });
  const approveTask = useAsyncTask({ task: approveAffiliationRequest });
  const rejectTask = useAsyncTask({ task: discardAffiliationRequest });

  const { run: runLoad } = loadTask;
  const reload = useCallback(async () => {
    try {
      await runLoad();
    } catch (error) {
      console.error("useProvidedAffiliations.reload", error);
      toast.error("Não foi possível carregar as organizações atendidas.");
    }
  }, [runLoad]);

  useEffect(() => {
    if (providerToken) void reload();
  }, [providerToken, reload]);

  const { run: runCreate } = createTask;
  const addReceiver = useCallback(
    async (receiver: AffiliationOrganization): Promise<AffiliationActionResult> => {
      try {
        await runCreate(receiver);
        toast.success(`${receiver.name} agora recebe sua teleconsultoria.`);
        void reload();
        return { isSuccess: true };
      } catch (error) {
        return { isSuccess: false, errorMessage: toErrorMessage(error) };
      }
    },
    [runCreate, reload],
  );

  const { run: runToggle } = toggleTask;
  const setRowActive = useCallback(
    async (row: TeleconsultoriaAffiliationRow, isActive: boolean) => {
      setPendingRowId(row.id);
      try {
        await runToggle({ affiliation: row.affiliation, isActive });
        toast.success(isActive ? "Afiliação reativada." : "Afiliação desativada.");
        await reload();
      } catch (error) {
        toast.error(toErrorMessage(error));
      } finally {
        setPendingRowId(null);
      }
    },
    [runToggle, reload],
  );

  // Pedido da afiliada: aprovar (ativa) ou recusar (apaga). Devolve true quando deu certo.
  const { run: runApprove } = approveTask;
  const { run: runReject } = rejectTask;
  const answerRequest = useCallback(
    async (row: TeleconsultoriaAffiliationRow, isApproved: boolean): Promise<boolean> => {
      setPendingRowId(row.id);
      try {
        await (isApproved ? runApprove(row.affiliation) : runReject(row.affiliation));
        toast.success(
          isApproved
            ? `${row.receiverName} agora recebe sua teleconsultoria.`
            : `Pedido de ${row.receiverName} recusado.`,
        );
        await reload();
        return true;
      } catch (error) {
        toast.error(toErrorMessage(error));
        return false;
      } finally {
        setPendingRowId(null);
      }
    },
    [runApprove, runReject, reload],
  );

  return {
    providerToken,
    rows: loadTask.data ?? EMPTY_ROWS,
    isLoading: loadTask.loading,
    isCreating: createTask.loading,
    pendingRowId,
    reload,
    addReceiver,
    setRowActive,
    answerRequest,
  };
}
